<template>
  <iColumn width="fill" height="hug" gap="none">
    <iRichText
      ref="richTextElement"
      v-model="value"
      :handle-link-modal-open="handleLinkModalOpen"
      :show-bubble-menu="true"
      :show-toolbar="false"
      :style-overrides="inputStyleOverrides"
      :text-style-overrides="textStyleOverridesValue"
      :tools-to-disable="['text-align', 'bullet-list']"
      :width="getOptionValue(LayoutWidth)"
      break-word
      height="hug"
      placeholder="Enter text here"
    />

    <teleport to="body">
      <RichTextDestinationPickerModal
        :link-data="linkData"
        :original-link-value="originalLinkData.linkValue"
        :publication-id="useFanPublicationStore().publicationId"
        :rich-text-ref="$refs.richTextElement"
        :show="showLinkModal"
        @close="handleLinkModalClose"
        @update="linkData = {...linkData, ...$event}"
      />
    </teleport>
  </iColumn>
</template>

<script>
import { DynamicElementMixin } from "%/dynamic-component-editor/mixins/dynamic-element-mixin";
import DynamicElementWithContext from "%/dynamic-component-editor/mixins/dynamic-element-with-context-mixin";
import useFanPublicationStore from "%/dynamic-component-editor/stores/fan-publication-store";
import LayoutWidth from "%/dynamic-component-editor/options/layout-width";
import RichTextDestinationPickerModal from "%/dynamic-component-editor/components/RichTextDestinationPickerModal.vue";
import DynamicAppInputOptionsMixin from "%/dynamic-component-editor/standard-elements/dynamic-app-elements/inputs/dynamic-app-input-options-mixin";

import LayoutWidthHeightOptions from "%/dynamic-component-editor/bundles/layout-width-height-options";
import BasicTextOptions from "%/dynamic-component-editor/bundles/basic-text-options";
import SpacingOptions from "%/dynamic-component-editor/bundles/spacing-options";
import AppearanceOptions from "%/dynamic-component-editor/bundles/appearance-options";
import BackgroundColor from "%/dynamic-component-editor/options/background-color";
import normalizeFontSizeStyleValue from "%/dynamic-component-editor/helpers/font-size-style-value";
import BorderColor from "%/dynamic-component-editor/options/border-color";
import BorderWidth from "%/dynamic-component-editor/options/border-width";

export default {
  name: "DynamicAppRichTextInputElement",
  components: { RichTextDestinationPickerModal },
  mixins: [DynamicElementMixin, DynamicElementWithContext, DynamicAppInputOptionsMixin],
  elementPanelSettings: {
    displayName: "Text",
    elementGroup: "general",
    displayIcon: "pencil",
    elementType: "element",
  },
  emits: ["update:modelValue"],
  data() {
    return {
      showLinkModal: false,
      linkData: {},
      originalLinkData: {},
    };
  },
  hidden: true,
  options: [
    BackgroundColor,
    BorderColor,
    BorderWidth,
  ],
  optionBundles: [
    new BasicTextOptions({}, {
      "font-family": true,
    }),
    new LayoutWidthHeightOptions({}, {
      "layout-height": true,
      "max-width": true,
      "max-height": true,
      "min-width": true,
      "min-height": true,
    }),
    new SpacingOptions(),
    new AppearanceOptions({}, {
      "border-radius": false,
    }),
  ],
  computed: {
    LayoutWidth() {
      return LayoutWidth;
    },
    // iRichText's styleGuide takes the bare keys directly; the previous
    // hardcoded values remain the fallbacks when the option is unset.
    inputStyleOverrides() {
      const padding = this.appInputPadding;
      return this.appInputStyleOverrides({
        paddingLeft: padding.left ?? "small",
        paddingRight: padding.right ?? "small",
        paddingTop: padding.top ?? "1px",
        paddingBottom: padding.bottom ?? "1px",
        placeholderFontSize: "small",
        borderWidth: this.appInputBorderWidth ?? "standard",
        borderColor: this.appInputBorderColor,
        borderRadius: this.appInputBorderRadius,
        backgroundColor: this.appInputBackgroundColor,
        fontColor: this.appInputFontColor,
      });
    },
    textStyleOverridesValue() {
      const fontSize = normalizeFontSizeStyleValue(this.appInputFontSize ?? "small", {
        element: "DynamicAppRichTextInputElement",
        option: "input-font-size",
      });
      return {
        standardFontSize: fontSize,
        standardHoverFontSize: fontSize,
      };
    },
    isEditable() {
      return true;
    },
    value: {
      get() {
        try {
          return JSON.parse(this.valueFromContext);
        } catch (e) {
          return this.valueFromContext;
        }
      },
      set(value) {
        if (!this.editMode) {
          this.valueFromContext = JSON.stringify(value);
        }
      },
    },
  },
  methods: {
    useFanPublicationStore,
    handleLinkModalOpen(event) {
      this.linkData = event;
      this.originalLinkData = event;
      this.showLinkModal = true;
    },
    handleLinkModalClose() {
      this.linkData = {};
      this.originalLinkData = {};
      this.showLinkModal = false;
    },
    handleChange(value) {
      this.value = value;
      this.$emit("update:modelValue", value);
    },
  },
  styleGuide: () => ({
    smallSpacing: { "size.spacing": "small" },
  }),
};
</script>

<style lang="scss" scoped>
:deep(.i-text) {
  width: -webkit-fill-available !important;
  width: -moz-available !important;
  line-height: 2 !important;
  word-break: break-all;
}
</style>
